<template>
    <div ref="chartdiv" :style="{ width: props.width, height: props.height }"></div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, defineProps } from 'vue';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5radar from '@amcharts/amcharts5/radar';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import _ from 'lodash';

const chartdiv = ref(null);
let cnt = 0;
const props = defineProps({
    chartData: {
        default: [],
    },
    width: {
        default: '100%',
    },
    height: {
        default: '100%',
    },
    chartFontSizeData: { default: null },
});

let root;
let chart;

const dataSet = (cData) => {
    if (cData && cData.length > 0) {
        root = am5.Root.new(chartdiv.value);

        // Set themes
        // https://www.amcharts.com/docs/v5/concepts/themes/
        root.setThemes([am5themes_Animated.new(root)]);

        // Create chart
        // https://www.amcharts.com/docs/v5/charts/radar-chart/
        chart = root.container.children.push(
            am5radar.RadarChart.new(root, {
                panX: false,
                panY: false,
                // wheelX: "panX",
                // wheelY: "zoomX",
                // innerRadius: am5.percent(40),
                radius: am5.percent(90),
                arrangeTooltips: false,
                paddingLeft: 20,
                paddingRight: 20,
            }),
        );

        // var cursor = chart.set(
        //     'cursor',
        //     am5radar.RadarCursor.new(root, {
        //         // behavior: "zoomX"
        //     }),
        // );

        // cursor.lineY.set('visible', false);

        // Create axes and their renderers
        // https://www.amcharts.com/docs/v5/charts/radar-chart/#Adding_axes
        var xRenderer = am5radar.AxisRendererCircular.new(root, {
            minGridDistance: 20,
        });
        xRenderer.labels.template.setAll({
            textType: 'adjusted',
            radius: 5,
            // paddingTop: 0,
            // paddingBottom: 0,
            // centerY: am5.p50,
            fontSize: props.chartFontSizeData ? props.chartFontSizeData.radarChartText : '2.2vmin',
            fontWeight: props.chartFontSizeData ? props.chartFontSizeData.fontWeight : 500,
            fill: '#000',
        });

        xRenderer.grid.template.setAll({
            location: 0.5,
            strokeDasharray: [0, 0],
        });

        var xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                maxDeviation: 0,
                categoryField: 'name',
                renderer: xRenderer,
                // tooltip: am5.Tooltip.new(root, {}),
                fill: 'red',
            }),
        );

        var yRenderer = am5radar.AxisRendererRadial.new(root, {
            renderer: am5radar.AxisRendererRadial.new(root, {}),
            minGridDistance: 20,
        });

        var yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: yRenderer,
                fill: 'red',
                min: 0,
                max: 100,
            }),
        );

        yAxis.get('renderer').labels.template.set('forceHidden', true);

        yRenderer.grid.template.setAll({
            strokeDasharray: [0, 0],
        });

        // Create series
        // https://www.amcharts.com/docs/v5/charts/radar-chart/#Adding_series
        var series1 = chart.series.push(
            am5radar.RadarLineSeries.new(root, {
                name: '',
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: 'value',
                categoryXField: 'name',
                fill: '#18b73f82',
                // tooltip: am5.Tooltip.new(root, {
                //     labelText: "{valueY}"
                // })
                // fillOpacity: 0.3
            }),
        );

        if (cnt > 1) {
            series1.strokes.template.setAll({
                strokeOpacity: 0,
            });

            series1.fills.template.setAll({
                visible: true,
                fillOpacity: 0.2,
            });
        } else {
            series1.strokes.template.setAll({
                strokeWidth: 2,
            });
            series1.bullets.push(function () {
                return am5.Bullet.new(root, {
                    sprite: am5.Circle.new(root, {
                        radius: 5,
                        fill: series1.get('fill'),
                    }),
                });
            });
        }

        let data = cData;
        // var legend = chart.radarContainer.children.push(
        //   am5.Legend.new(root, {
        //     width: 150,
        //     centerX: am5.p50,
        //     centerY: am5.p50
        //   })
        // );
        // legend.data.setAll([series1]);

        // Set data
        // https://www.amcharts.com/docs/v5/charts/radar-chart/#Setting_data

        series1.data.setAll(data);
        xAxis.data.setAll(data);

        // Animate chart and series in
        // https://www.amcharts.com/docs/v5/concepts/animations/#Initial_animation
        series1.appear(2000);
        chart.appear(1000, 100);
        // chart.appear(1000, 100);
    }
};

onMounted(() => {
    cnt = _.filter([...props.chartData], (i) => {
        return i.value > 0;
    }).length;
    const tmpData = _.sortBy(props.chartData, 'value');
    dataSet(tmpData);
});

watch(
    () => props.chartData,
    (newValue) => {
        cnt = _.filter([...newValue], (i) => {
            return i.value > 0;
        }).length;
        const tmpData = _.sortBy(newValue, 'value');
        dataSet(tmpData);
    },
);

onUnmounted(() => {
    root.dispose();

    // console.log('width:', props.width, 'height:', props.height);
});
</script>

<style scoped lang="scss"></style>
